import { useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { clearError, clearSuccess } from '../../redux/Slices/authSlice';

/**
 * Custom hook لمعالجة التوستات (success/error) بشكل موحد في كل الصفحات
 * 
 * هذا الـ hook يحل مشاكل:
 * - عدم ظهور التوستات في البيئة الإنتاجية
 * - تراكم التوستات عند تسجيل الخروج
 * - تكرار التوستات
 * 
 * @param {Object} options - خيارات التوست
 * @param {string|null} options.success - رسالة النجاح من Redux
 * @param {string|null} options.error - رسالة الخطأ من Redux
 * @param {Function} options.clearSuccessAction - دالة مسح success (افتراضي: clearSuccess)
 * @param {Function} options.clearErrorAction - دالة مسح error (افتراضي: clearError)
 * @param {Function} options.onSuccess - callback عند النجاح (اختياري)
 * @param {Function} options.onError - callback عند الخطأ (اختياري)
 * @param {number} options.successDelay - تأخير قبل مسح success بالمللي ثانية (افتراضي: 100)
 * @param {number} options.errorDelay - تأخير قبل مسح error بالمللي ثانية (افتراضي: 3000)
 * @param {number} options.successAutoClose - وقت إغلاق توست النجاح (افتراضي: 3000)
 * @param {number} options.errorAutoClose - وقت إغلاق توست الخطأ (افتراضي: 5000)
 * 
 * @example
 * // استخدام بسيط
 * const { success, error } = useSelector((state) => state.auth);
 * useToastNotifications({ success, error });
 * 
 * @example
 * // مع callback عند النجاح
 * useToastNotifications({
 *   success,
 *   error,
 *   onSuccess: () => {
 *     dispatch(getPaymentVouchers({ page: currentPage, per_page: 10 }));
 *   }
 * });
 */
export const useToastNotifications = ({
  success,
  error,
  clearSuccessAction,
  clearErrorAction,
  onSuccess,
  onError,
  successDelay = 100,
  errorDelay = 3000,
  successAutoClose = 3000,
  errorAutoClose = 5000,
} = {}) => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  
  // Refs لمنع تكرار التوستات
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const lastErrorRef = useRef({ message: null, time: 0 });

  // إعدادات موحدة للتوستات - محسنة للبيئة الإنتاجية
  const toastConfig = useMemo(() => ({
    position: "top-right",
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    pauseOnFocusLoss: false, // مهم للبيئة الإنتاجية
    draggable: true,
    rtl: isRTL,
  }), [isRTL]);

  // معالجة رسائل النجاح
  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      
      // منع تكرار التوستات (نفس الرسالة في أقل من ثانيتين)
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, {
          ...toastConfig,
          autoClose: successAutoClose,
        });
        
        lastSuccessRef.current = { message: success, time: now };
        
        // استدعاء callback إذا كان موجود
        if (onSuccess) {
          onSuccess(success);
        }
        
        // تأخير بسيط قبل مسح success لضمان ظهور التوست في البيئة الإنتاجية
        setTimeout(() => {
          const clearAction = clearSuccessAction || clearSuccess;
          dispatch(clearAction());
        }, successDelay);
      }
    }
  }, [success, dispatch, isRTL, successAutoClose, successDelay, clearSuccessAction, onSuccess, toastConfig]);

  // معالجة رسائل الخطأ
  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      
      // منع تكرار التوستات (نفس الرسالة في أقل من ثانيتين)
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, {
          ...toastConfig,
          autoClose: errorAutoClose,
        });
        
        lastErrorRef.current = { message: error, time: now };
        
        // استدعاء callback إذا كان موجود
        if (onError) {
          onError(error);
        }
        
        // تأخير قبل مسح error
        setTimeout(() => {
          const clearAction = clearErrorAction || clearError;
          dispatch(clearAction());
        }, errorDelay);
      }
    }
  }, [error, dispatch, isRTL, errorAutoClose, errorDelay, clearErrorAction, onError, toastConfig]);

  return {
    // يمكن إرجاع دوال مساعدة إذا لزم الأمر
  };
};

